import React, {useEffect, useMemo, useState} from "react";
import {CenterSpinner, useEvent, useLazyQuery} from "@soap/modules";
import {StandaloneLayout} from "../StandaloneLayout";
import {useParams} from "react-router";
import {Card, CardContent, CardHeader, CardTitle} from "../../ui/card";
import {viewStates} from "./view-states";
import {Welcome} from "./welcome";
import {ViewSummary} from "./view-summary";
import {Instruction} from "./instruction";
// Removed ConfirmStudentDetails - StudentId now comes from SharedAssessment.LinkData
import {AnswerQuestion} from "./answer-question";
import useCountDown from '../../../hooks/useCountdown';
import {SectionLanding} from "./section-landing";
import {useQueryResponse} from "../../../hooks/useQueryString";


const SESSION_STORAGE_KEY = 'assessmentResultId';
const SESSION_STORAGE_LINK_KEY = 'assessmentLinkId';

export function TakeAssessment() {

    let {sharedAssessmentLinkId} = useParams();
    const [activeQuestion, setActiveQuestion] = useState();
    const [activeSection, setActiveSection] = useState();
    const [viewState, setViewState] = useState(viewStates.loading);
    const [completedAssessmentDetails, setCompletedAssessmentDetails] = useState();
    const [timeLeft, timerControls] = useCountDown(0);
    const [resumeAssessmentResultId, setResumeAssessmentResultId] = useState(() => {
        // Check sessionStorage on mount - only resume if link ID matches
        const storedLinkId = sessionStorage.getItem(SESSION_STORAGE_LINK_KEY);
        const storedResultId = sessionStorage.getItem(SESSION_STORAGE_KEY);
        
        // Debug logging to help diagnose sessionStorage issues
        if (process.env.NODE_ENV === 'development') {
            console.log('TakeAssessment mount - sessionStorage check:', {
                storedLinkId,
                sharedAssessmentLinkId,
                storedResultId,
                match: storedLinkId === sharedAssessmentLinkId
            });
        }
        
        if (storedLinkId && storedResultId) {
            // If we have stored data, check if it matches the current URL param
            // Only clear if we have a URL param AND it doesn't match
            if (sharedAssessmentLinkId && storedLinkId !== sharedAssessmentLinkId) {
                // Clear stale data if link doesn't match
                sessionStorage.removeItem(SESSION_STORAGE_KEY);
                sessionStorage.removeItem(SESSION_STORAGE_LINK_KEY);
                return null;
            }
            // If we have stored data and either no URL param or it matches, use it
            // This handles the case where useParams() might not be ready yet
            if (!sharedAssessmentLinkId || storedLinkId === sharedAssessmentLinkId) {
                return storedResultId;
            }
        }
        return null;
    });
    // Don't provide initial query - we'll call manually to avoid auto-triggering both
    const [getSharedAssessment, e170] = useLazyQuery("C212v1_GetSharedAssessmentByLinkId");
    const [resumeAssessment] = useLazyQuery("C223v1_ResumeAssessment");
    const hasInstructions = useMemo(() => e170?.e170_Instructions?.trim().length > 0, [e170]);
    
    // Track if we've already attempted to load to prevent duplicate calls
    const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
    
    // On mount, check if we should resume or start fresh
    useEffect(() => {
        if (hasAttemptedLoad) return; // Only run once
        
        // Re-check sessionStorage in case useParams wasn't ready during useState initialization
        if (!resumeAssessmentResultId && sharedAssessmentLinkId) {
            const storedLinkId = sessionStorage.getItem(SESSION_STORAGE_LINK_KEY);
            const storedResultId = sessionStorage.getItem(SESSION_STORAGE_KEY);
            if (storedLinkId === sharedAssessmentLinkId && storedResultId) {
                setResumeAssessmentResultId(storedResultId);
                setHasAttemptedLoad(true);
                // Fetch e170 data first, then resume
                getSharedAssessment({
                    C212_SharedAssessmentId: sharedAssessmentLinkId,
                    C212_SharedAssessmentLinkId: sharedAssessmentLinkId
                });
                resumeAssessment({
                    C223_AssessmentResultId: storedResultId
                });
                return;
            }
        }
        
        if (resumeAssessmentResultId) {
            // Resume existing assessment - also fetch e170 data
            setHasAttemptedLoad(true);
            getSharedAssessment({
                C212_SharedAssessmentId: sharedAssessmentLinkId,
                C212_SharedAssessmentLinkId: sharedAssessmentLinkId
            });
            resumeAssessment({
                C223_AssessmentResultId: resumeAssessmentResultId
            });
        } else if (sharedAssessmentLinkId) {
            // Start fresh - get shared assessment
            setHasAttemptedLoad(true);
            getSharedAssessment({
                C212_SharedAssessmentId: sharedAssessmentLinkId,
                C212_SharedAssessmentLinkId: sharedAssessmentLinkId
            });
        }
    }, [resumeAssessmentResultId, sharedAssessmentLinkId, hasAttemptedLoad]);

    useQueryResponse(e170, e => {
        if (viewState === viewStates.loading) {
            // If assessment is already completed, show completion message
            if (e?.e170_IsCompleted) {
                setViewState(viewStates.alreadyCompleted);
                return;
            }
            
            // If E170 includes an AssessmentResultId, it means the SharedAssessment is archived
            // but there's a resumable AssessmentResult - automatically resume it
            // Note: e170 data is now available, so we can resume
            if (e?.e170_AssessmentResultId && !resumeAssessmentResultId) {
                // Store in sessionStorage and resume
                sessionStorage.setItem(SESSION_STORAGE_KEY, e.e170_AssessmentResultId);
                sessionStorage.setItem(SESSION_STORAGE_LINK_KEY, sharedAssessmentLinkId);
                setResumeAssessmentResultId(e.e170_AssessmentResultId);
                resumeAssessment({
                    C223_AssessmentResultId: e.e170_AssessmentResultId
                });
            } else if (!resumeAssessmentResultId) {
                setViewState(viewStates.welcome);
            }
        }
    })

    useEvent({
        eventName: "E172v1_AssessmentStarted",
        onEventReceived: (e172) => {
            // Store AssessmentResultId and link ID in sessionStorage when assessment starts
            if (e172.e172_AssessmentResultId) {
                sessionStorage.setItem(SESSION_STORAGE_KEY, e172.e172_AssessmentResultId);
                sessionStorage.setItem(SESSION_STORAGE_LINK_KEY, sharedAssessmentLinkId);
                setResumeAssessmentResultId(e172.e172_AssessmentResultId);
            }
        }
    })

    useEvent({
        eventName: "E173v1_GotQuestionDetails",
        onEventReceived: (e173) => {
            setActiveQuestion(e173);
            setViewState(viewStates.showQuestion);
        }
    })

    useEvent({
        eventName: "E174v1_AssessmentCompleted",
        onEventReceived: (e174) => {
            // Clear sessionStorage when assessment completes
            sessionStorage.removeItem(SESSION_STORAGE_KEY);
            sessionStorage.removeItem(SESSION_STORAGE_LINK_KEY);
            setResumeAssessmentResultId(null);
            setCompletedAssessmentDetails(e174);
            setViewState(viewStates.assessmentCompleted);
        }
    })

    useEvent({
        eventName: "E175v1_NewSectionStarted",
        onEventReceived: (e175) => {
            setActiveSection(e175);
            setViewState(viewStates.sectionLanding);
        }
    })

    switch (viewState) {
        case viewStates.loading:
            return <StandaloneLayout showHeader>
                <div className="flex items-center justify-center min-h-screen">
                    <CenterSpinner/>
                </div>
            </StandaloneLayout>;
        case viewStates.welcome:
            return <StandaloneLayout showHeader>
                <div className="flex items-center justify-center min-h-screen p-4">
                    <Card className="w-full max-w-2xl">
                        <CardContent className="p-6">
                            <Welcome e170={e170} setViewState={setViewState} hasInstructions={hasInstructions}/>
                        </CardContent>
                    </Card>
                </div>
            </StandaloneLayout>;
        case viewStates.showInstructions:
            return <StandaloneLayout showHeader>
                <div className="flex items-center justify-center min-h-screen p-4">
                    <Card className="w-full max-w-2xl">
                        <CardHeader>
                            <CardTitle className="text-center text-xl font-semibold">{e170.e170_AssessmentTitle}</CardTitle>
                        </CardHeader>
                        <CardContent className="p-6">
                            <Instruction html={e170.e170_Instructions} setViewState={setViewState} e170={e170}/>
                        </CardContent>
                    </Card>
                </div>
            </StandaloneLayout>;
        // Removed confirmStudentDetails case - StudentId now comes from SharedAssessment.LinkData
        case viewStates.showQuestion:
            return <StandaloneLayout showHeader>
                <div className="flex items-center justify-center min-h-screen p-4">
                    <Card className="w-full max-w-2xl">
                        <CardHeader>
                            <CardTitle className="text-center text-xl font-semibold">{e170?.e170_AssessmentTitle || activeQuestion?.e173_SectionTitle || 'Assessment'}</CardTitle>
                        </CardHeader>
                        <CardContent className="p-6">
                            {/*key is important on AnswerQuestion or state will be reused from question to question otherwise*/}
                            <AnswerQuestion key={activeQuestion?.e173_NextQuestionId} e173={activeQuestion} timeLeft={timeLeft}
                                            timerControls={timerControls}/>
                        </CardContent>
                    </Card>
                </div>
            </StandaloneLayout>;

        case viewStates.sectionLanding:
            return <StandaloneLayout showHeader>
                <div className="flex items-center justify-center min-h-screen p-4">
                    <Card className="w-full max-w-2xl">
                        <CardContent className="p-6">
                            <SectionLanding key={activeSection?.e175_SectionId} e175={activeSection} timeLeft={timeLeft} timerControls={timerControls} e170={e170} />
                        </CardContent>
                    </Card>
                </div>
            </StandaloneLayout>;

        case viewStates.assessmentCompleted:
            return <StandaloneLayout showHeader>
                <div className="flex items-center justify-center min-h-screen p-4">
                    <Card className="w-full max-w-2xl">
                        <CardContent className="p-6">
                            <ViewSummary e174={completedAssessmentDetails} title={e170?.e170_AssessmentTitle || completedAssessmentDetails?.e174_AssessmentTitle || 'Assessment'} isPreview={e170?.e170_IsPreview || false} e170={e170}/>
                        </CardContent>
                    </Card>
                </div>
            </StandaloneLayout>;

        case viewStates.alreadyCompleted:
            return <StandaloneLayout showHeader>
                <div className="flex items-center justify-center min-h-screen p-4">
                    <Card className="w-full max-w-2xl">
                        <CardContent className="p-6">
                            <div className="space-y-6 text-center">
                                <div className="space-y-4">
                                    <h2 className="text-2xl font-semibold text-gray-900">
                                        Assessment Already Completed
                                    </h2>
                                    <h3 className="text-xl font-medium text-gray-700">
                                        {e170?.e170_AssessmentTitle || 'Assessment'}
                                    </h3>
                                </div>
                                
                                <div className="space-y-4">
                                    <p className="text-lg text-gray-700">
                                        This assessment has already been completed.
                                    </p>
                                    <p className="text-lg text-gray-700">
                                        You may now close this browser window.
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </StandaloneLayout>;
    }
}



